// @flow

/**
 * PDF Download Service
 * Converts markdown to PDF using pdfmake and triggers download.
 * Supports embedded chart images via {{CHART_IMAGE:id}} tokens.
 */

import { markdownToPdfmake } from '../utils/markdownToPdfmake';
import { prepareMarkdownForExport } from '../utils/prepareMarkdownForExport';
import { applyStandardFontMetricsToVfs } from './standardFontMetricsVfs';

let pdfMakeInstance = null;

/**
 * Lazy-load pdfmake and initialize fonts
 */
const getPdfMake = async () => {
  if (pdfMakeInstance) {
    return pdfMakeInstance;
  }

  // Dynamically import pdfmake
  const pdfMakeModule = await import('pdfmake/build/pdfmake');

  const pdfMake = pdfMakeModule.default || pdfMakeModule;

  applyStandardFontMetricsToVfs(pdfMake);

  // Prefer standard PDF fonts (no embedded font data) for maximum
  // compatibility with Adobe Reader across platforms.
  // See: "Standard 14" fonts (Helvetica/Times/Courier + variants).
  pdfMake.fonts = {
    Helvetica: {
      normal: 'Helvetica',
      bold: 'Helvetica-Bold',
      italics: 'Helvetica-Oblique',
      bolditalics: 'Helvetica-BoldOblique',
    },
    Courier: {
      normal: 'Courier',
      bold: 'Courier-Bold',
      italics: 'Courier-Oblique',
      bolditalics: 'Courier-BoldOblique',
    },
  };

  pdfMakeInstance = pdfMake;
  return pdfMake;
};

/**
 * Convert markdown text to PDF and trigger download
 * @param {string} markdownText - The markdown content to convert (may contain {{CHART_IMAGE:id}} tokens)
 * @param {string} filename - The filename for the download
 * @param {Object} chartImages - Optional map of displayId → PNG data URL for chart embedding
 */
export const downloadAsPdf = async (
  markdownText: string,
  filename: string,
  chartImages?: { [string]: string },
): Promise<void> => {
  const docDefinition = markdownToPdfmake(
    prepareMarkdownForExport(markdownText),
    chartImages,
  );
  const pdfMake = await getPdfMake();
  const pdfDoc = pdfMake.createPdf(docDefinition);

  // Wait for PDF document to be fully generated before triggering download
  await pdfDoc.pdfDocumentPromise;

  // Use pdfmake's native download (same mechanism that worked before heading changes)
  pdfDoc.download(filename);
};
